import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { fetchData } from '../../../helpers/offthreadFetcher';
import { configureUrlWithParams } from '../../../helpers/utils';
import type { RootState } from '../../../state/store';

const useFetchBSWidgetData = ({
  url,
  shouldFetch = true,
}: {
  url: string;
  shouldFetch?: boolean;
}) => {
  const { filters, company } = useSelector((state: RootState) => state);
  const timezoneOffset = company?.company_details?.time_zone_offset;
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const isMounted = useRef(true);
  const lastFetchedEndpointRef = useRef<string | null>(null);
  const apiEndpoint = useMemo(() => {
    return configureUrlWithParams(`/api/v1/dar/business-summary/${url}/widget`, {
      ...filters,
      shouldUseEpocForTimeRange: true,
      timezoneOffset
    });
  }, [url, JSON.stringify(filters)]);

  const fetchWidgetData = useCallback(async () => {
    if (!apiEndpoint) {
      return undefined;
    }
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetchData(apiEndpoint);
      if (isMounted.current) {
        setData(response);
        lastFetchedEndpointRef.current = apiEndpoint;
      }
      return response;
    } catch (err) {
      if (isMounted.current) {
        setError(err);
      }
      return undefined;
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
      }
    }
  }, [apiEndpoint]);

  useEffect(() => {
    isMounted.current = true;
    if (shouldFetch && lastFetchedEndpointRef.current !== apiEndpoint) {
      fetchWidgetData();
    }
    return () => {
      isMounted.current = false;
    };
  }, [fetchWidgetData, shouldFetch, apiEndpoint]);

  const mutate = useCallback(
    async (_data?: any, options?: { revalidate?: boolean }) => {
      if (options && options.revalidate === false) {
        return data;
      }
      return fetchWidgetData();
    },
    [data, fetchWidgetData]
  );

  return { data, error, isLoading, mutate };
};

export { useFetchBSWidgetData };
